// EspaciosManager.js — CRUD de espacios (salones/jardines) por venue, FRA-294.
//
// Dos modos de persistencia:
// - Con `venueId`: cada operación escribe la subcolección venues/{id}/espacios
//   y el rollup del doc del venue en un mismo writeBatch. `onVenuePatch`
//   avisa al padre para que parchee su formValues Y su snapshot inicial —
//   sin eso, un "Guardar cambios" posterior del formulario del venue pisaría
//   el rollup con una copia vieja.
// - Sin `venueId` (alta de venue, el doc todavía no existe): la lista vive en
//   el estado del padre vía `espacios`/`onEspaciosChange` y el padre escribe
//   todo al crear el venue.
//
// Invariantes que esta UI garantiza (el modelo los asume, ver
// utils/espacios.js): slug/ID inmutable después de crear; `componentes` solo
// referencia átomos; `incompatibleCon` solo entre átomos y simétrica; un
// compuesto nunca lleva arista propia.

import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  collection,
  doc,
  getDocs,
  updateDoc,
  writeBatch,
  serverTimestamp,
  Timestamp,
} from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "./AuthProvider";
import { useEspacios } from "../hooks/useEspacios";
import { Input } from "./input";
import { Switch } from "./switch";
import { Checkbox } from "./checkbox";
import { Select } from "./select";
import { Button } from "./button";
import {
  Dialog,
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogLabel,
} from "./dialog";
import BilingualDescriptionField from "./BilingualDescriptionField";
import CatalogCheckboxGroup from "./CatalogCheckboxGroup";
import { ImagesField } from "./ImagesField";
import { showAlert } from "../alertService";
import { logBitacora, BITACORA_ACTIONS } from "../utils/bitacora";
import { calcularRollup } from "../utils/espacios";
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  LockClosedIcon,
} from "@heroicons/react/20/solid";

const ACCENT = "#1b4d2e";

function slugify(str) {
  return (str || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");
}

function uniqueSlug(base, takenIds) {
  if (!takenIds.includes(base)) return base;
  let n = 2;
  while (takenIds.includes(`${base}-${n}`)) n += 1;
  return `${base}-${n}`;
}

// Comparación estable independiente del orden de llaves que devuelva
// Firestore, para escribir solo los docs que de verdad cambiaron.
function stableStringify(value) {
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(",")}]`;
  if (value && typeof value === "object") {
    return `{${Object.keys(value)
      .sort()
      .map((k) => `${JSON.stringify(k)}:${stableStringify(value[k])}`)
      .join(",")}}`;
  }
  return JSON.stringify(value);
}

const sortEspacios = (list) =>
  [...list].sort(
    (a, b) =>
      (a.orden ?? 0) - (b.orden ?? 0) ||
      (a.nombre || "").localeCompare(b.nombre || "")
  );

const esAtomico = (e) => e.esAtomico !== false;

const numOrNull = (v) => {
  if (v === "" || v == null) return null;
  const n = Number(v);
  return isNaN(n) ? null : n;
};

const fechaEvento = (ev) => {
  let ini = ev.startTime || ev.start;
  if (ini && ini.toDate) ini = ini.toDate();
  else if (typeof ini === "string" || typeof ini === "number") ini = new Date(ini);
  return ini instanceof Date && !isNaN(ini)
    ? ini.toLocaleDateString("es-MX", { day: "numeric", month: "short", year: "numeric" })
    : "";
};

const capacidadResumen = (e) => {
  const c = e.capacidad || {};
  const parts = [];
  if (c.sentadosMax != null)
    parts.push(
      c.sentadosMin != null
        ? `${c.sentadosMin}–${c.sentadosMax} sentados`
        : `${c.sentadosMax} sentados`
    );
  if (c.piePMax != null)
    parts.push(
      c.piePMin != null ? `${c.piePMin}–${c.piePMax} de pie` : `${c.piePMax} de pie`
    );
  if (e.superficieM2 != null) parts.push(`${e.superficieM2} m²`);
  return parts.join(" · ");
};

const EMPTY_FORM = {
  nombre: "",
  descripcion: "",
  descripcionEn: "",
  esAtomico: true,
  componentes: [],
  sentadosMin: "",
  sentadosMax: "",
  piePMin: "",
  piePMax: "",
  superficieM2: "",
  amenidades: [],
  fotos: [],
  activo: true,
  reservable: true,
};

const EspaciosManager = ({
  venueId,
  storageVenueId,
  espacios: espaciosProp,
  onEspaciosChange,
  permiteEventosSimultaneos = true,
  onPermiteChange,
  basicInfo,
  onVenuePatch,
  catalogs,
  readOnly = false,
  // Flag admin (adminConfig/features.multiespacio). Apagado, el venue captura
  // espacios SOLO para presumirlos en su ficha: se ocultan la reserva por
  // separado, la composición y las incompatibilidades, y los espacios nuevos
  // nacen `reservable: false`. Los que ya existían conservan su valor, así
  // encender el flag no vuelve rentable un baño por accidente.
  multiespacioActivo = false,
}) => {
  const { userInfo, user } = useAuth();
  // La lectura vive en la capa de datos (useEspacios, onSnapshot): el diff de
  // persist y el rollup siempre se calculan contra la subcolección viva, no
  // contra una copia del momento de montar.
  const { espacios: espaciosLive, loading, error: espaciosError } = useEspacios(venueId);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const formVacio = useMemo(
    () => ({ ...EMPTY_FORM, reservable: multiespacioActivo }),
    [multiespacioActivo]
  );
  const [form, setForm] = useState(formVacio);
  const [deleting, setDeleting] = useState(null);
  // Eventos FUTUROS (no cancelados) que referencian el espacio a borrar o los
  // compuestos que caerían en cascada. null = verificando; "error" = no se
  // pudo leer. Con alguno, el borrado se bloquea: su footprint congelado
  // (`unidadesOcupadas`) quedaría huérfano y el evento dejaría de bloquear
  // el calendario y la disponibilidad pública.
  const [eventosFuturos, setEventosFuturos] = useState(null);
  // Par en construcción para agregar una incompatibilidad.
  const [parA, setParA] = useState("");
  const [parB, setParB] = useState("");

  const isLocal = !venueId;
  const list = useMemo(
    () => sortEspacios(isLocal ? espaciosProp || [] : espaciosLive),
    [isLocal, espaciosProp, espaciosLive]
  );

  useEffect(() => {
    if (espaciosError) showAlert("Error al cargar los espacios.", "error");
  }, [espaciosError]);
  // Solo los atómicos RESERVABLES entran a composición e incompatibilidad;
  // un área de showcase (reservable: false) no puede estorbar ni componer.
  const atomicos = useMemo(
    () => list.filter((e) => esAtomico(e) && e.reservable !== false),
    [list]
  );
  const porId = useMemo(() => {
    const map = {};
    list.forEach((e) => {
      map[e.id] = e;
    });
    return map;
  }, [list]);

  // Pares declarados, deduplicados (la arista se guarda simétrica en ambos
  // docs) y en el orden de la lista para que la UI sea estable.
  const paresIncompatibles = useMemo(() => {
    const idsAtomicos = new Set(atomicos.map((a) => a.id));
    const vistos = new Set();
    const pares = [];
    atomicos.forEach((a) => {
      (a.incompatibleCon || []).forEach((otroId) => {
        if (!idsAtomicos.has(otroId)) return;
        const key = [a.id, otroId].sort().join("|");
        if (vistos.has(key)) return;
        vistos.add(key);
        pares.push([a.id, otroId]);
      });
    });
    return pares;
  }, [atomicos]);

  // Compuestos que heredan la incompatibilidad de un par (contienen a uno de
  // los dos átomos) — solo para explicarlo en la UI, no se guarda nada.
  const compuestosDelPar = useCallback(
    (aId, bId) =>
      list.filter(
        (e) =>
          !esAtomico(e) &&
          ((e.componentes || []).includes(aId) ||
            (e.componentes || []).includes(bId))
      ),
    [list]
  );

  // Persiste la lista completa: escribe por diff los docs que cambiaron,
  // borra los que desaparecieron y reescribe el rollup del venue — todo en
  // un solo batch para que nada quede a medias. `extraOps(batch)` permite
  // colgar escrituras adicionales del mismo batch (cascada a galerías).
  const persist = useCallback(
    async (next, log, extraOps) => {
      const normalized = sortEspacios(next).map((e, idx) => ({ ...e, orden: idx }));
      if (isLocal) {
        onEspaciosChange(normalized);
        return true;
      }
      setSaving(true);
      try {
        const batch = writeBatch(db);
        const prevById = {};
        espaciosLive.forEach((e) => {
          prevById[e.id] = e;
        });
        const nextIds = new Set(normalized.map((e) => e.id));
        normalized.forEach((e) => {
          const prev = prevById[e.id];
          if (!prev || stableStringify(prev) !== stableStringify(e)) {
            const { id, ...data } = e;
            batch.set(doc(db, "venues", venueId, "espacios", id), data);
          }
        });
        espaciosLive.forEach((e) => {
          if (!nextIds.has(e.id)) {
            batch.delete(doc(db, "venues", venueId, "espacios", e.id));
          }
        });
        const rollup = calcularRollup(normalized);
        const venueUpdates = { ...rollup, lastUpdate: serverTimestamp() };
        let basicInfo3;
        // basicInfo["3"] se sigue derivando para los filtros de capacidad
        // existentes. Solo cuando hay un máximo y basicInfo no es el shape
        // legacy de lista (ese no se toca).
        if (rollup.capacidadMax != null && !Array.isArray(basicInfo)) {
          basicInfo3 = String(rollup.capacidadMax);
          venueUpdates["basicInfo.3"] = basicInfo3;
        }
        batch.update(doc(db, "venues", venueId), venueUpdates);
        if (extraOps) extraOps(batch);
        // No hay estado local que actualizar: el onSnapshot de useEspacios
        // refleja el batch al instante (latency compensation).
        await batch.commit();
        if (onVenuePatch) onVenuePatch({ fields: rollup, basicInfo3 });
        if (log) logBitacora(venueId, log, userInfo, user);
        return true;
      } catch (err) {
        console.error("Error guardando espacios:", err);
        showAlert("Error al guardar los espacios.", "error");
        return false;
      } finally {
        setSaving(false);
      }
    },
    [isLocal, onEspaciosChange, espaciosLive, venueId, basicInfo, onVenuePatch, userInfo, user]
  );

  const openCreate = () => {
    setEditingId(null);
    setForm(formVacio);
    setDialogOpen(true);
  };

  const openEdit = (e) => {
    setEditingId(e.id);
    setForm({
      nombre: e.nombre || "",
      descripcion: e.descripcion || "",
      descripcionEn: e.descripcionEn || "",
      esAtomico: esAtomico(e),
      componentes: e.componentes || [],
      sentadosMin: e.capacidad?.sentadosMin ?? "",
      sentadosMax: e.capacidad?.sentadosMax ?? "",
      piePMin: e.capacidad?.piePMin ?? "",
      piePMax: e.capacidad?.piePMax ?? "",
      superficieM2: e.superficieM2 ?? "",
      amenidades: e.amenidades || [],
      fotos: e.fotos || [],
      activo: e.activo !== false,
      reservable: e.reservable !== false,
    });
    setDialogOpen(true);
  };

  const slugPreview = editingId || uniqueSlug(slugify(form.nombre) || "", list.map((e) => e.id));

  const handleSave = async () => {
    const nombre = form.nombre.trim();
    if (!nombre) {
      showAlert("El nombre del espacio no puede estar vacío.", "error");
      return;
    }
    const editing = editingId ? porId[editingId] : null;
    if (!editing && !slugify(nombre)) {
      showAlert("El nombre debe contener al menos una letra o número.", "error");
      return;
    }
    if (!form.esAtomico && form.componentes.length === 0) {
      showAlert("Un espacio compuesto necesita al menos un componente.", "error");
      return;
    }
    if (editing && esAtomico(editing) && (!form.esAtomico || !form.reservable)) {
      const referenciadoPor = list.filter(
        (e) => !esAtomico(e) && (e.componentes || []).includes(editing.id)
      );
      if (referenciadoPor.length > 0) {
        showAlert(
          `Este espacio es componente de ${referenciadoPor
            .map((e) => e.nombre)
            .join(", ")}. Quítalo de ahí antes de cambiarle el tipo.`,
          "error"
        );
        return;
      }
    }

    const id = editing ? editing.id : uniqueSlug(slugify(nombre), list.map((e) => e.id));
    // Un área no reservable siempre es atómica, sin componentes ni aristas.
    const esReservable = form.reservable;
    const esAtomicoFinal = esReservable ? form.esAtomico : true;
    const espacio = {
      id,
      nombre,
      slug: id,
      descripcion: form.descripcion,
      descripcionEn: form.descripcionEn,
      esAtomico: esAtomicoFinal,
      reservable: esReservable,
      componentes: esAtomicoFinal ? [] : form.componentes,
      incompatibleCon:
        esAtomicoFinal && esReservable ? editing?.incompatibleCon || [] : [],
      capacidad: {
        sentadosMin: numOrNull(form.sentadosMin),
        sentadosMax: numOrNull(form.sentadosMax),
        piePMin: numOrNull(form.piePMin),
        piePMax: numOrNull(form.piePMax),
      },
      superficieM2: numOrNull(form.superficieM2),
      amenidades: form.amenidades,
      fotos: form.fotos,
      orden: editing ? editing.orden ?? 0 : list.length,
      activo: form.activo,
      createdAt: editing?.createdAt || Timestamp.now(),
    };

    let next = editing
      ? list.map((e) => (e.id === editing.id ? espacio : e))
      : [...list, espacio];
    // Un compuesto o un área no reservable nunca llevan arista propia: al
    // convertir se limpia su incompatibilidad de ambos lados.
    if (editing && (!form.esAtomico || !form.reservable)) {
      next = next.map((e) =>
        (e.incompatibleCon || []).includes(id)
          ? { ...e, incompatibleCon: e.incompatibleCon.filter((x) => x !== id) }
          : e
      );
    }

    const ok = await persist(next, {
      action: editing ? BITACORA_ACTIONS.ESPACIO_EDIT : BITACORA_ACTIONS.ESPACIO_CREATE,
      entity: "espacio",
      entityId: id,
      entityName: nombre,
      details: {},
    });
    if (ok) setDialogOpen(false);
  };

  // Cascada al borrar: fuera de `componentes` e `incompatibleCon` de todos,
  // y los compuestos que queden vacíos se borran también (avisando antes).
  const deleteCascade = useCallback(
    (esp) => {
      const compuestosVaciados = list.filter(
        (e) =>
          !esAtomico(e) &&
          (e.componentes || []).includes(esp.id) &&
          e.componentes.length === 1
      );
      const idsBorrados = new Set([esp.id, ...compuestosVaciados.map((e) => e.id)]);
      const next = list
        .filter((e) => !idsBorrados.has(e.id))
        .map((e) => ({
          ...e,
          componentes: (e.componentes || []).filter((c) => !idsBorrados.has(c)),
          incompatibleCon: (e.incompatibleCon || []).filter((c) => !idsBorrados.has(c)),
        }));
      return { next, compuestosVaciados };
    },
    [list]
  );

  // Al abrir el diálogo de borrado se revisan los eventos del calendario: un
  // getDocs puntual (no snapshot) filtrado en cliente — array-contains +
  // rango de fecha pediría índice compuesto, y esto solo corre al confirmar
  // un borrado. Ante error se bloquea (fail-closed): mejor no borrar que
  // dejar footprints huérfanos sin saberlo.
  useEffect(() => {
    if (!deleting || isLocal) {
      setEventosFuturos(deleting ? [] : null);
      return undefined;
    }
    let vigente = true;
    setEventosFuturos(null);
    const { compuestosVaciados } = deleteCascade(deleting);
    const ids = new Set([deleting.id, ...compuestosVaciados.map((e) => e.id)]);
    const ahora = new Date();
    getDocs(collection(db, "venues", venueId, "calendars", "default", "events"))
      .then((snap) => {
        if (!vigente) return;
        const futuros = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((ev) => {
            if (ev.status === "cancelado") return false;
            const refs = [
              ...(ev.unidadesOcupadas || []),
              ...(ev.espaciosIds || []),
            ];
            if (!refs.some((x) => ids.has(x))) return false;
            let fin = ev.endTime || ev.end || ev.startTime || ev.start;
            if (fin && fin.toDate) fin = fin.toDate();
            else if (typeof fin === "string" || typeof fin === "number")
              fin = new Date(fin);
            return fin instanceof Date && !isNaN(fin) && fin >= ahora;
          });
        setEventosFuturos(futuros);
      })
      .catch((err) => {
        console.error("Error verificando eventos del espacio:", err);
        if (vigente) setEventosFuturos("error");
      });
    return () => {
      vigente = false;
    };
  }, [deleting, isLocal, venueId, deleteCascade]);

  // Alternativa al borrado bloqueado: desactivar. El espacio sale de la ficha
  // pública pero sus docs (y los footprints de los eventos) quedan intactos.
  const handleDesactivar = async () => {
    const esp = deleting;
    if (!esp) return;
    const next = list.map((e) =>
      e.id === esp.id ? { ...e, activo: false } : e
    );
    const ok = await persist(next, {
      action: BITACORA_ACTIONS.ESPACIO_EDIT,
      entity: "espacio",
      entityId: esp.id,
      entityName: esp.nombre || esp.id,
      details: { desactivado: true },
    });
    if (ok) {
      setDeleting(null);
      showAlert("Espacio desactivado: ya no se muestra en la ficha pública.", "success");
    }
  };

  const handleDelete = async () => {
    const esp = deleting;
    if (!esp || borradoBloqueado) return;
    const { next, compuestosVaciados } = deleteCascade(esp);
    const idsBorrados = [esp.id, ...compuestosVaciados.map((e) => e.id)];
    // Cascada a galerías (FRA-300): el espacio borrado sale de
    // `espaciosUsados` en el mismo batch. Los eventos del calendario que ya
    // lo referencian en unidadesOcupadas NO se tocan (footprint histórico).
    let extraOps = null;
    if (venueId) {
      try {
        const galSnap = await getDocs(collection(db, "venues", venueId, "galerias"));
        const afectadas = galSnap.docs.filter((d) =>
          ((d.data().espaciosUsados || [])).some((x) => idsBorrados.includes(x))
        );
        if (afectadas.length > 0) {
          extraOps = (batch) => {
            afectadas.forEach((d) => {
              batch.update(d.ref, {
                espaciosUsados: (d.data().espaciosUsados || []).filter(
                  (x) => !idsBorrados.includes(x)
                ),
              });
            });
          };
        }
      } catch (err) {
        console.error("Error leyendo galerías para la cascada:", err);
      }
    }
    const ok = await persist(
      next,
      {
        action: BITACORA_ACTIONS.ESPACIO_DELETE,
        entity: "espacio",
        entityId: esp.id,
        entityName: esp.nombre || esp.id,
        details: {},
      },
      extraOps
    );
    if (ok) setDeleting(null);
  };

  const move = (esp, dir) => {
    const idx = list.findIndex((e) => e.id === esp.id);
    const target = idx + dir;
    if (target < 0 || target >= list.length) return;
    const next = [...list];
    [next[idx], next[target]] = [next[target], next[idx]];
    persist(
      next.map((e, i) => ({ ...e, orden: i })),
      {
        action: BITACORA_ACTIONS.ESPACIO_EDIT,
        entity: "espacio",
        entityId: esp.id,
        entityName: esp.nombre || esp.id,
        details: { reorden: true },
      }
    );
  };

  const toggleIncompatibilidad = (aId, bId, checked) => {
    const applyEdge = (e) => {
      if (e.id !== aId && e.id !== bId) return e;
      const otro = e.id === aId ? bId : aId;
      const actual = e.incompatibleCon || [];
      const nueva = checked
        ? actual.includes(otro)
          ? actual
          : [...actual, otro].sort()
        : actual.filter((x) => x !== otro);
      return { ...e, incompatibleCon: nueva };
    };
    persist(list.map(applyEdge), {
      action: BITACORA_ACTIONS.ESPACIO_INCOMPATIBILIDAD_EDIT,
      entity: "espacio",
      entityId: aId,
      entityName: `${porId[aId]?.nombre || aId} ✗ ${porId[bId]?.nombre || bId}`,
      details: { par: [aId, bId], incompatibles: checked },
    });
  };

  const agregarPar = () => {
    if (!parA || !parB || parA === parB) return;
    toggleIncompatibilidad(parA, parB, true);
    setParA("");
    setParB("");
  };

  const togglePermite = async (checked) => {
    if (isLocal) {
      onPermiteChange(checked);
      return;
    }
    setSaving(true);
    try {
      await updateDoc(doc(db, "venues", venueId), {
        permiteEventosSimultaneos: checked,
        lastUpdate: serverTimestamp(),
      });
      if (onVenuePatch) onVenuePatch({ fields: { permiteEventosSimultaneos: checked } });
      logBitacora(
        venueId,
        {
          action: BITACORA_ACTIONS.VENUE_EVENTOS_SIMULTANEOS_TOGGLE,
          entity: "venue",
          entityId: venueId,
          entityName: "",
          details: { permiteEventosSimultaneos: checked },
        },
        userInfo,
        user
      );
    } catch (err) {
      console.error("Error actualizando eventos simultáneos:", err);
      showAlert("Error al actualizar la configuración.", "error");
    } finally {
      setSaving(false);
    }
  };

  const componentesLabel = (e) =>
    (e.componentes || [])
      .map((c) => porId[c]?.nombre || c)
      .join(" + ");

  const futurosBloqueando = Array.isArray(eventosFuturos) ? eventosFuturos : [];
  // Bloqueado mientras se verifica, si la lectura falló, o si hay futuros.
  const borradoBloqueado = !Array.isArray(eventosFuturos) || futurosBloqueando.length > 0;

  const deletingCascade = deleting ? deleteCascade(deleting) : null;
  const deletingReferencias = deleting
    ? list.filter(
        (e) =>
          !esAtomico(e) &&
          e.id !== deleting.id &&
          (e.componentes || []).includes(deleting.id)
      )
    : [];

  if (loading) {
    return <div className="text-sm text-zinc-500">Cargando espacios…</div>;
  }

  const fotosVenueId = storageVenueId || venueId;

  return (
    <div className="space-y-6">
      {/* Lista de espacios */}
      {list.length === 0 ? (
        <p className="text-sm text-zinc-500">
          Aún no has dado de alta espacios. Si tu venue tiene salones, jardines o
          terrazas que se rentan por separado, agrégalos aquí — cada uno con su
          capacidad, fotos y disponibilidad propia.
        </p>
      ) : (
        <ul className="divide-y divide-zinc-100 rounded-lg border border-zinc-200">
          {list.map((e, idx) => (
            <li key={e.id} className="flex items-center gap-3 px-3 py-2.5">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium text-zinc-900">{e.nombre}</span>
                  {!esAtomico(e) && (
                    <span className="inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-[11px] font-medium text-blue-700 ring-1 ring-blue-200">
                      {componentesLabel(e)}
                    </span>
                  )}
                  {e.reservable === false && (
                    <span className="inline-flex items-center rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700 ring-1 ring-amber-200">
                      Área (no se reserva)
                    </span>
                  )}
                  {e.activo === false && (
                    <span className="inline-flex items-center rounded-full bg-zinc-100 px-2 py-0.5 text-[11px] font-medium text-zinc-500">
                      Inactivo
                    </span>
                  )}
                </div>
                <div className="mt-0.5 flex flex-wrap gap-x-3 text-xs text-zinc-500">
                  <span className="font-mono">/{e.slug || e.id}</span>
                  {capacidadResumen(e) && <span>{capacidadResumen(e)}</span>}
                </div>
              </div>
              {!readOnly && (
                <div className="flex shrink-0 items-center gap-1">
                  <button
                    type="button"
                    onClick={() => move(e, -1)}
                    disabled={idx === 0 || saving}
                    className="rounded p-1 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700 disabled:opacity-30"
                    title="Subir"
                  >
                    <ChevronUpIcon className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => move(e, 1)}
                    disabled={idx === list.length - 1 || saving}
                    className="rounded p-1 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700 disabled:opacity-30"
                    title="Bajar"
                  >
                    <ChevronDownIcon className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => openEdit(e)}
                    disabled={saving}
                    className="rounded p-1 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700"
                    title="Editar"
                  >
                    <PencilIcon className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setDeleting(e)}
                    disabled={saving}
                    className="rounded p-1 text-zinc-400 hover:bg-red-50 hover:text-red-600"
                    title="Eliminar"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {!readOnly && (
        <button
          type="button"
          onClick={openCreate}
          disabled={saving}
          className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-semibold text-white shadow-sm transition hover:opacity-90 disabled:opacity-50"
          style={{ backgroundColor: ACCENT }}
        >
          <PlusIcon className="h-4 w-4" />
          Agregar espacio
        </button>
      )}

      {/* Eventos simultáneos + reglas de incompatibilidad. Sin el flag no hay
          reserva por salón, así que ninguna de las dos cosas significa nada. */}
      {list.length > 0 && multiespacioActivo && (
        <div className="space-y-4 border-t border-zinc-100 pt-4">
          <label className="flex items-center justify-between gap-3">
            <span>
              <span className="block text-sm font-medium text-zinc-900">
                Permitir eventos simultáneos
              </span>
              <span className="block text-xs text-zinc-500">
                Apágalo si nunca rentas dos espacios a la vez: cualquier evento
                bloqueará el venue completo.
              </span>
            </span>
            <Switch
              color="accent"
              checked={permiteEventosSimultaneos}
              onChange={togglePermite}
              disabled={readOnly || saving}
            />
          </label>

          {atomicos.length >= 2 && (
            <div>
              <div className="text-sm font-medium text-zinc-900">
                Espacios que no pueden reservarse al mismo tiempo
              </div>
              <p className="mt-0.5 text-xs text-zinc-500">
                Agrega los pares que se estorban entre sí (por accesos, ruido o
                estacionamiento).
                {!permiteEventosSimultaneos &&
                  " Con eventos simultáneos apagados, todos los espacios se bloquean entre sí — estas reglas no aplican."}
              </p>
              <div
                className={
                  !permiteEventosSimultaneos ? "pointer-events-none opacity-40" : ""
                }
              >
                {paresIncompatibles.length === 0 ? (
                  <p className="mt-3 rounded-lg border border-dashed border-zinc-200 px-3 py-2.5 text-sm text-zinc-500">
                    No has marcado ninguno — todos tus espacios pueden operar a
                    la vez.
                  </p>
                ) : (
                  <ul className="mt-3 divide-y divide-zinc-100 rounded-lg border border-zinc-200">
                    {paresIncompatibles.map(([aId, bId]) => {
                      const heredados = compuestosDelPar(aId, bId);
                      return (
                        <li
                          key={`${aId}|${bId}`}
                          className="flex items-center gap-3 px-3 py-2.5"
                        >
                          <div className="min-w-0 flex-1 text-sm text-zinc-700">
                            Si se reserva{" "}
                            <span className="font-medium text-zinc-900">
                              {porId[aId]?.nombre || aId}
                            </span>
                            ,{" "}
                            <span className="font-medium text-zinc-900">
                              {porId[bId]?.nombre || bId}
                            </span>{" "}
                            no está disponible{" "}
                            <span className="text-zinc-500">(y viceversa)</span>
                            {heredados.length > 0 && (
                              <span className="mt-0.5 block text-xs text-zinc-500">
                                También aplica a{" "}
                                {heredados
                                  .map((c) => {
                                    const dentro = [aId, bId]
                                      .filter((id) =>
                                        (c.componentes || []).includes(id)
                                      )
                                      .map((id) => porId[id]?.nombre || id)
                                      .join(" y ");
                                    return `${c.nombre} (incluye ${dentro})`;
                                  })
                                  .join(", ")}
                              </span>
                            )}
                          </div>
                          {!readOnly && (
                            <button
                              type="button"
                              onClick={() =>
                                toggleIncompatibilidad(aId, bId, false)
                              }
                              disabled={saving || !permiteEventosSimultaneos}
                              className="shrink-0 rounded p-1 text-zinc-400 hover:bg-red-50 hover:text-red-600"
                              title="Quitar"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}

                {!readOnly &&
                  paresIncompatibles.length <
                    (atomicos.length * (atomicos.length - 1)) / 2 && (
                    <div className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-2 rounded-lg bg-zinc-50 px-3 py-2.5 text-sm text-zinc-700">
                      <span>Si se reserva</span>
                      <div className="w-40">
                        <Select
                          value={parA}
                          onChange={(e) => {
                            setParA(e.target.value);
                            setParB("");
                          }}
                          disabled={saving || !permiteEventosSimultaneos}
                        >
                          <option value="">un espacio…</option>
                          {atomicos.map((a) => (
                            <option key={a.id} value={a.id}>
                              {a.nombre}
                            </option>
                          ))}
                        </Select>
                      </div>
                      <span>,</span>
                      <div className="w-40">
                        <Select
                          value={parB}
                          onChange={(e) => setParB(e.target.value)}
                          disabled={
                            !parA || saving || !permiteEventosSimultaneos
                          }
                        >
                          <option value="">otro espacio…</option>
                          {atomicos
                            .filter(
                              (b) =>
                                b.id !== parA &&
                                !(porId[parA]?.incompatibleCon || []).includes(
                                  b.id
                                )
                            )
                            .map((b) => (
                              <option key={b.id} value={b.id}>
                                {b.nombre}
                              </option>
                            ))}
                        </Select>
                      </div>
                      <span>no está disponible.</span>
                      <Button
                        outline
                        onClick={agregarPar}
                        disabled={
                          !parA ||
                          !parB ||
                          saving ||
                          !permiteEventosSimultaneos
                        }
                      >
                        Agregar regla
                      </Button>
                    </div>
                  )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Dialog crear/editar */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} size="3xl">
        <DialogTitle>{editingId ? "Editar espacio" : "Nuevo espacio"}</DialogTitle>
        <DialogBody className="space-y-5">
          <div className="grid gap-4 sm:grid-cols-[1fr_auto]">
            <div>
              <DialogLabel>Nombre</DialogLabel>
              <div className="mt-1">
                <Input
                  value={form.nombre}
                  onChange={(e) => setForm((f) => ({ ...f, nombre: e.target.value }))}
                  placeholder="Salón Principal"
                />
              </div>
              <p className="mt-1.5 flex items-center gap-1 text-xs text-zinc-400">
                {editingId && <LockClosedIcon className="h-3 w-3" />}
                URL: <span className="font-mono">/espacios/{slugPreview || "…"}</span>
                {editingId
                  ? " — fija desde que se creó el espacio"
                  : " — se fija al crear y ya no se puede cambiar"}
              </p>
            </div>
            <label className="flex items-center gap-2 self-start pt-6">
              <Switch
                color="accent"
                checked={form.activo}
                onChange={(checked) => setForm((f) => ({ ...f, activo: checked }))}
              />
              <span className="text-sm text-zinc-700">
                {form.activo ? "Activo" : "Inactivo"}
              </span>
            </label>
          </div>

          <BilingualDescriptionField
            valueEs={form.descripcion}
            valueEn={form.descripcionEn}
            onChangeEs={(v) => setForm((f) => ({ ...f, descripcion: v }))}
            onChangeEn={(v) => setForm((f) => ({ ...f, descripcionEn: v }))}
            rows={3}
          />

          {multiespacioActivo ? (
            <div>
              <label className="flex items-center gap-2">
                <Switch
                  color="accent"
                  checked={form.reservable}
                  onChange={(checked) => setForm((f) => ({ ...f, reservable: checked }))}
                />
                <span className="text-sm font-medium text-zinc-900">
                  Se reserva por separado
                </span>
              </label>
              <p className="mt-0.5 text-xs text-zinc-500">
                Apágalo para áreas que solo quieres presumir (cocina, suite,
                lobby): aparecen en tu página pública con sus fotos, pero no
                entran al calendario ni a la disponibilidad.
              </p>
            </div>
          ) : (
            <p className="rounded-lg bg-zinc-50 px-3 py-2 text-xs text-zinc-500">
              Este espacio aparecerá en tu página pública con sus fotos y su
              capacidad. La reserva por separado (calendario y disponibilidad
              por salón) no está activa en este venue.
            </p>
          )}

          {form.reservable && (
          <div>
            <label className="flex items-center gap-2">
              <Switch
                color="accent"
                checked={!form.esAtomico}
                onChange={(checked) =>
                  setForm((f) => ({
                    ...f,
                    esAtomico: !checked,
                    componentes: checked ? f.componentes : [],
                  }))
                }
              />
              <span className="text-sm font-medium text-zinc-900">
                Espacio compuesto
              </span>
            </label>
            <p className="mt-0.5 text-xs text-zinc-500">
              Un compuesto es la unión de otros espacios (ej. Salón Principal =
              Salón B + Salón C). Ocuparlo ocupa sus partes, y al revés.
            </p>
            {!form.esAtomico && (
              <div className="mt-2 space-y-1.5 rounded-lg border border-zinc-200 p-3">
                {atomicos.filter((a) => a.id !== editingId).length === 0 ? (
                  <p className="text-xs text-zinc-500">
                    No hay otros espacios simples para combinar. Crea primero los
                    espacios individuales.
                  </p>
                ) : (
                  atomicos
                    .filter((a) => a.id !== editingId)
                    .map((a) => {
                      const checked = form.componentes.includes(a.id);
                      return (
                        <label key={a.id} className="flex items-center gap-2">
                          <Checkbox
                            color="accent"
                            checked={checked}
                            onChange={(c) =>
                              setForm((f) => ({
                                ...f,
                                componentes: c
                                  ? [...f.componentes, a.id]
                                  : f.componentes.filter((x) => x !== a.id),
                              }))
                            }
                          />
                          <span className="text-sm text-zinc-700">{a.nombre}</span>
                        </label>
                      );
                    })
                )}
              </div>
            )}
          </div>
          )}

          <div>
            <DialogLabel>Capacidad (personas)</DialogLabel>
            <div className="mt-1 grid grid-cols-2 gap-3 sm:grid-cols-4">
              <div>
                <span className="text-xs text-zinc-500">Sentados mín.</span>
                <Input
                  type="number"
                  min="0"
                  value={form.sentadosMin}
                  onChange={(e) => setForm((f) => ({ ...f, sentadosMin: e.target.value }))}
                />
              </div>
              <div>
                <span className="text-xs text-zinc-500">Sentados máx.</span>
                <Input
                  type="number"
                  min="0"
                  value={form.sentadosMax}
                  onChange={(e) => setForm((f) => ({ ...f, sentadosMax: e.target.value }))}
                />
              </div>
              <div>
                <span className="text-xs text-zinc-500">De pie mín.</span>
                <Input
                  type="number"
                  min="0"
                  value={form.piePMin}
                  onChange={(e) => setForm((f) => ({ ...f, piePMin: e.target.value }))}
                />
              </div>
              <div>
                <span className="text-xs text-zinc-500">De pie máx.</span>
                <Input
                  type="number"
                  min="0"
                  value={form.piePMax}
                  onChange={(e) => setForm((f) => ({ ...f, piePMax: e.target.value }))}
                />
              </div>
            </div>
          </div>

          <div className="max-w-[12rem]">
            <DialogLabel>Superficie (m²)</DialogLabel>
            <div className="mt-1">
              <Input
                type="number"
                min="0"
                value={form.superficieM2}
                onChange={(e) => setForm((f) => ({ ...f, superficieM2: e.target.value }))}
              />
            </div>
          </div>

          <div>
            <DialogLabel>Amenidades del espacio</DialogLabel>
            <div className="mt-2 max-h-48 overflow-y-auto rounded-lg border border-zinc-200 p-3">
              <CatalogCheckboxGroup
                catalog={catalogs?.instalaciones || []}
                selectedIds={form.amenidades}
                onChange={(ids) => setForm((f) => ({ ...f, amenidades: ids }))}
              />
            </div>
          </div>

          {fotosVenueId ? (
            <ImagesField
              label="Fotos del espacio"
              images={form.fotos}
              onReorderImages={(fotos) => setForm((f) => ({ ...f, fotos }))}
              venueId={fotosVenueId}
            />
          ) : (
            <p className="text-xs text-zinc-500">
              Escribe primero el nombre del venue para poder subir fotos del
              espacio.
            </p>
          )}
        </DialogBody>
        <DialogActions>
          <Button plain type="button" onClick={() => setDialogOpen(false)}>
            Cancelar
          </Button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="rounded-lg px-4 py-1.5 text-sm font-semibold text-white shadow-sm transition hover:opacity-90 disabled:opacity-50"
            style={{ backgroundColor: ACCENT }}
          >
            {saving ? "Guardando…" : editingId ? "Guardar cambios" : "Crear espacio"}
          </button>
        </DialogActions>
      </Dialog>

      {/* Dialog confirmar borrado */}
      <Dialog open={!!deleting} onClose={() => setDeleting(null)} size="lg">
        <DialogTitle>Eliminar espacio</DialogTitle>
        <DialogBody className="space-y-3 text-sm text-zinc-700">
          <p>
            ¿Eliminar <span className="font-semibold">{deleting?.nombre}</span>?
            Esta acción no se puede deshacer y su dirección pública dejará de
            existir.
          </p>
          {deletingReferencias.length > 0 && (
            <p>
              Se quitará de los componentes de{" "}
              <span className="font-medium">
                {deletingReferencias.map((e) => e.nombre).join(", ")}
              </span>
              .
            </p>
          )}
          {deletingCascade?.compuestosVaciados.length > 0 && (
            <p className="rounded-lg bg-amber-50 px-3 py-2 text-amber-800 ring-1 ring-amber-200">
              {deletingCascade.compuestosVaciados.map((e) => e.nombre).join(", ")}{" "}
              se quedaría sin componentes y también se eliminará.
            </p>
          )}
          {eventosFuturos === "error" && (
            <p className="rounded-lg bg-red-50 px-3 py-2 text-red-800 ring-1 ring-red-200">
              No se pudieron verificar los eventos del calendario, así que el
              borrado está bloqueado. Cierra este diálogo e intenta de nuevo.
            </p>
          )}
          {futurosBloqueando.length > 0 && (
            <div className="space-y-2 rounded-lg bg-red-50 px-3 py-2 text-red-800 ring-1 ring-red-200">
              <p className="font-medium">
                No se puede eliminar:{" "}
                {futurosBloqueando.length === 1
                  ? "un evento futuro ocupa"
                  : `${futurosBloqueando.length} eventos futuros ocupan`}{" "}
                este espacio.
              </p>
              <ul className="list-inside list-disc">
                {futurosBloqueando.slice(0, 5).map((ev) => (
                  <li key={ev.id}>
                    {ev.nombre || "Sin nombre"}
                    {fechaEvento(ev) ? ` — ${fechaEvento(ev)}` : ""}
                  </li>
                ))}
                {futurosBloqueando.length > 5 && (
                  <li>y {futurosBloqueando.length - 5} más…</li>
                )}
              </ul>
              <p>
                Si ya no ofreces este espacio, mejor <span className="font-medium">desactívalo</span>:
                deja de mostrarse en la ficha pública y los eventos conservan su
                ocupación. Si un evento va a cambiar de espacio, edítalo primero
                y vuelve a intentar.
              </p>
            </div>
          )}
          <p className="text-xs text-zinc-500">
            Los eventos pasados que ocuparon este espacio no se modifican.
          </p>
        </DialogBody>
        <DialogActions>
          <Button plain type="button" onClick={() => setDeleting(null)}>
            Cancelar
          </Button>
          {futurosBloqueando.length > 0 && deleting?.activo !== false && (
            <button
              type="button"
              onClick={handleDesactivar}
              disabled={saving}
              className="rounded-lg px-4 py-1.5 text-sm font-semibold text-white shadow-sm transition hover:opacity-90 disabled:opacity-50"
              style={{ backgroundColor: ACCENT }}
            >
              {saving ? "Desactivando…" : "Desactivar espacio"}
            </button>
          )}
          <Button
            color="red"
            type="button"
            onClick={handleDelete}
            disabled={saving || borradoBloqueado}
          >
            {saving
              ? "Eliminando…"
              : eventosFuturos === null
                ? "Verificando eventos…"
                : "Eliminar"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default EspaciosManager;
